import { z } from 'zod';
import { emailSchema, isCurrency, isTimeZone } from './schemas.js';

/**
 * Commerce Event contract. Every integration (REST API, SDKs, WooCommerce, Shopify, browser)
 * is translated into these normalized events before anything else in Cartly sees it.
 */
export const COMMERCE_EVENT_TYPES = [
  'product.created',
  'product.updated',
  'inventory.updated',
  'customer.created',
  'customer.updated',
  'cart.created',
  'cart.updated',
  'cart.abandoned',
  'checkout.started',
  'checkout.abandoned',
  'order.created',
  'order.paid',
  'order.cancelled',
  'order.fulfilled',
  'order.delivered',
  'order.deleted',
  'refund.created',
] as const;
export type CommerceEventType = (typeof COMMERCE_EVENT_TYPES)[number];

/** Events a browser (publishable key) may send. Everything else needs a server-side key. */
export const STOREFRONT_EVENT_TYPES = [
  'cart.created',
  'cart.updated',
  'checkout.started',
] as const satisfies readonly CommerceEventType[];

export const EVENT_SOURCES_FROM_CLIENTS = ['api', 'sdk_node', 'sdk_php', 'sdk_python'] as const;

// ── Primitives ────────────────────────────────────────────────────────────

const MONEY_RE = /^-?\d{1,15}(\.\d{1,4})?$/;

/** Decimal amount as a string with up to 4 decimals. Accepts numbers or numeric strings. */
export const moneySchema = z
  .union([z.number().finite(), z.string().trim()])
  .transform((v) => (typeof v === 'number' ? stripZeros(v.toFixed(4)) : v))
  .refine((v) => MONEY_RE.test(v), 'Must be a decimal amount with at most 4 decimals');

function stripZeros(v: string): string {
  return v.includes('.') ? v.replace(/\.?0+$/, '') : v;
}

export const currencySchema = z
  .string()
  .trim()
  .regex(/^[A-Za-z]{3}$/, 'Use a 3-letter ISO 4217 currency code')
  .transform((v) => v.toUpperCase())
  .refine(isCurrency, 'Unknown currency');

/** Accepts common formatting ("+91 98765-43210") and normalizes to E.164. */
export const phoneSchema = z
  .string()
  .trim()
  .transform((v) => v.replace(/[\s\-().]/g, ''))
  .refine(
    (v) => /^\+[1-9]\d{6,14}$/.test(v),
    'Phone must be in international format, e.g. +919876543210',
  );

const externalIdSchema = z.string().trim().min(1).max(191);
const shortText = (max: number) => z.string().trim().max(max);
const httpUrlSchema = z.url({ protocol: /^https?$/ }).max(2048);
export const isoDateTimeSchema = z.iso.datetime({ offset: true });

// ── Entities ──────────────────────────────────────────────────────────────

export const customerRefSchema = z
  .object({
    externalId: externalIdSchema.optional(),
    email: emailSchema.optional(),
    phone: phoneSchema.optional(),
    firstName: shortText(120).optional(),
    lastName: shortText(120).optional(),
    locale: shortText(10).optional(),
    /** The customer agreed at checkout to receive WhatsApp/text messages (needs `phone`). */
    whatsappConsent: z.boolean().optional(),
  })
  .refine((c) => c.externalId || c.email || c.phone, {
    message: 'A customer needs at least one of externalId, email or phone',
  });

export const lineItemSchema = z.object({
  externalId: externalIdSchema.optional(),
  productExternalId: externalIdSchema.optional(),
  variantExternalId: externalIdSchema.optional(),
  sku: shortText(191).optional(),
  title: z.string().trim().min(1).max(512),
  quantity: z.number().int().min(0).max(1_000_000),
  unitPrice: moneySchema,
  total: moneySchema.optional(),
});

export const variantSchema = z.object({
  externalId: externalIdSchema,
  sku: shortText(191).optional(),
  title: shortText(255).optional(),
  price: moneySchema,
  compareAtPrice: moneySchema.optional(),
  inventoryQuantity: z.number().int().optional(),
  /** Platform inventory-item id; lets stock events address a variant they cannot name. */
  inventoryItemExternalId: externalIdSchema.optional(),
  options: z.record(z.string().max(64), z.string().max(255)).optional(),
});

export const productSchema = z.object({
  externalId: externalIdSchema,
  title: z.string().trim().min(1).max(512),
  handle: shortText(255).optional(),
  description: z.string().max(65_535).optional(),
  vendor: shortText(255).optional(),
  productType: shortText(255).optional(),
  status: z.enum(['active', 'draft', 'archived']).default('active'),
  url: httpUrlSchema.optional(),
  imageUrl: httpUrlSchema.optional(),
  tags: z.array(z.string().trim().max(100)).max(250).optional(),
  /** Omit for simple products; a default variant is created from `price`. */
  variants: z.array(variantSchema).max(500).optional(),
  price: moneySchema.optional(),
  deleted: z.boolean().optional(),
});

export const inventorySchema = z
  .object({
    variantExternalId: externalIdSchema.optional(),
    /** Alternative to variantExternalId (Shopify stock webhooks identify inventory items). */
    inventoryItemExternalId: externalIdSchema.optional(),
    locationExternalId: externalIdSchema.optional(),
    available: z.number().int(),
  })
  .refine((v) => v.variantExternalId || v.inventoryItemExternalId, {
    message: 'Provide variantExternalId or inventoryItemExternalId',
    path: ['variantExternalId'],
  });

export const checkoutSchema = z.object({
  externalId: externalIdSchema,
  customer: customerRefSchema.optional(),
  currency: currencySchema,
  subtotal: moneySchema.optional(),
  total: moneySchema,
  recoveryUrl: httpUrlSchema.optional(),
  lineItems: z.array(lineItemSchema).max(500),
});

export const orderSchema = z.object({
  externalId: externalIdSchema,
  orderNumber: shortText(64).optional(),
  customer: customerRefSchema.optional(),
  currency: currencySchema,
  subtotal: moneySchema.optional(),
  total: moneySchema,
  financialStatus: z
    .enum(['pending', 'authorized', 'paid', 'partially_refunded', 'refunded', 'voided'])
    .optional(),
  fulfillmentStatus: z.enum(['unfulfilled', 'partial', 'fulfilled', 'delivered']).optional(),
  paymentMethod: z.enum(['cod', 'prepaid', 'other']).optional(),
  /** Shipment details; sent with the fulfillment so shipping messages can carry tracking. */
  tracking: z
    .object({
      carrier: shortText(120).optional(),
      number: shortText(120).optional(),
      url: httpUrlSchema.optional(),
    })
    .optional(),
  /** Links the order to the cart/checkout it came from (used for recovery attribution). */
  checkoutExternalId: externalIdSchema.optional(),
  placedAt: isoDateTimeSchema.optional(),
  /** When the order was archived (closed) in the store; null = re-opened; omit when unknown. */
  closedAt: isoDateTimeSchema.nullable().optional(),
  lineItems: z.array(lineItemSchema).max(500).default([]),
});

export const refundSchema = z.object({
  externalId: externalIdSchema,
  orderExternalId: externalIdSchema,
  amount: moneySchema,
  currency: currencySchema,
  reason: shortText(255).optional(),
});

export const customerSchema = customerRefSchema.and(z.object({ externalId: externalIdSchema }));

// ── Event envelope ────────────────────────────────────────────────────────

const envelopeBase = {
  /** When the change happened in the source system. Defaults to the time Cartly receives it. */
  occurredAt: isoDateTimeSchema.optional(),
  /** Unique per store; a repeated key is accepted but ignored. Defaults to type:externalId:occurredAt. */
  idempotencyKey: z.string().trim().min(1).max(191).optional(),
  source: z.enum(EVENT_SOURCES_FROM_CLIENTS).optional(),
};

const ev = <T extends CommerceEventType, P extends z.ZodType>(type: T, payload: P) =>
  z.object({ type: z.literal(type), payload, ...envelopeBase });

const productPayload = z.object({ product: productSchema });
const cartPayload = z.object({ cart: checkoutSchema });
const checkoutPayload = z.object({ checkout: checkoutSchema });
const orderPayload = z.object({ order: orderSchema });
const orderDeletedPayload = z.object({ order: z.object({ externalId: externalIdSchema }) });
const customerPayload = z.object({ customer: customerSchema });

export const commerceEventInputSchema = z.discriminatedUnion('type', [
  ev('product.created', productPayload),
  ev('product.updated', productPayload),
  ev('inventory.updated', z.object({ inventory: inventorySchema })),
  ev('customer.created', customerPayload),
  ev('customer.updated', customerPayload),
  ev('cart.created', cartPayload),
  ev('cart.updated', cartPayload),
  ev('cart.abandoned', cartPayload),
  ev('checkout.started', checkoutPayload),
  ev('checkout.abandoned', checkoutPayload),
  ev('order.created', orderPayload),
  ev('order.paid', orderPayload),
  ev('order.cancelled', orderPayload),
  ev('order.fulfilled', orderPayload),
  ev('order.delivered', orderPayload),
  ev('order.deleted', orderDeletedPayload),
  ev('refund.created', z.object({ refund: refundSchema })),
]);
export type CommerceEventInput = z.output<typeof commerceEventInputSchema>;

export const commerceEventBatchSchema = z.object({
  events: z.array(z.unknown()).min(1).max(100),
});

/** The external resource id an event refers to. */
export function externalIdOf(event: CommerceEventInput): string {
  const p = event.payload as Record<
    string,
    { externalId?: string; variantExternalId?: string; inventoryItemExternalId?: string }
  >;
  switch (event.type) {
    case 'inventory.updated':
      return p['inventory']!.variantExternalId ?? p['inventory']!.inventoryItemExternalId!;
    case 'product.created':
    case 'product.updated':
      return p['product']!.externalId!;
    case 'customer.created':
    case 'customer.updated':
      return p['customer']!.externalId!;
    case 'cart.created':
    case 'cart.updated':
    case 'cart.abandoned':
      return p['cart']!.externalId!;
    case 'checkout.started':
    case 'checkout.abandoned':
      return p['checkout']!.externalId!;
    case 'refund.created':
      return p['refund']!.externalId!;
    default:
      return p['order']!.externalId!;
  }
}

export function defaultIdempotencyKey(type: string, externalId: string, occurredAt: Date): string {
  return `${type}:${externalId}:${occurredAt.toISOString()}`.slice(0, 191);
}

/** Shape delivered to merchant webhook endpoints and returned by the events API. */
export interface CommerceEventDto {
  id: string;
  storeId: string;
  type: CommerceEventType;
  occurredAt: string;
  externalId: string | null;
  source: string;
  idempotencyKey: string;
  status: 'pending' | 'processed' | 'failed';
  payload: unknown;
  createdAt: string;
}

// ── Stores, credentials, webhook endpoints (dashboard API) ───────────────

export const STORE_PLATFORMS = [
  'shopify',
  'woocommerce',
  'custom_php',
  'laravel',
  'django',
  'flask',
  'fastapi',
  'node',
  'other',
] as const;
export type StorePlatform = (typeof STORE_PLATFORMS)[number];

/** Accepts "https://Shop.Example.com/path" and returns "shop.example.com". */
export const domainSchema = z
  .string()
  .trim()
  .toLowerCase()
  .transform((v) =>
    v
      .replace(/^[a-z]+:\/\//, '')
      .replace(/[/?#].*$/, '')
      .replace(/:\d+$/, ''),
  )
  .refine(
    (v) => /^(?=.{1,253}$)([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(v),
    'Enter a domain like shop.example.com',
  );

export const createStoreSchema = z.object({
  name: z.string().trim().min(2).max(120),
  platform: z.enum(STORE_PLATFORMS),
  domain: domainSchema,
  currency: currencySchema,
  timezone: z.string().trim().max(64).refine(isTimeZone, 'Unknown time zone'),
});
export const updateStoreSchema = createStoreSchema.omit({ platform: true }).partial();

export const API_KEY_SCOPES = ['events:write', 'events:read'] as const;
export const originSchema = z
  .string()
  .trim()
  .toLowerCase()
  .refine(
    (v) => /^https?:\/\/[a-z0-9.-]+(:\d+)?$/.test(v),
    'Use an origin like https://shop.example.com',
  );

export const createCredentialSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('secret'),
    name: z.string().trim().min(1).max(80),
    scopes: z.array(z.enum(API_KEY_SCOPES)).min(1).default(['events:write', 'events:read']),
  }),
  z.object({
    kind: z.literal('publishable'),
    name: z.string().trim().min(1).max(80),
    allowedOrigins: z.array(originSchema).min(1).max(10),
  }),
]);

export const webhookEndpointSchema = z.object({
  url: z.url({ protocol: /^https?$/ }).max(2048),
  description: z.string().trim().max(255).default(''),
  storeId: z.string().length(26).nullable().default(null),
  events: z
    .array(z.union([z.enum(COMMERCE_EVENT_TYPES), z.literal('*')]))
    .min(1)
    .max(COMMERCE_EVENT_TYPES.length),
});
export const updateWebhookEndpointSchema = webhookEndpointSchema
  .omit({ storeId: true })
  .partial()
  .extend({ isActive: z.boolean().optional() });

export interface StoreDto {
  id: string;
  name: string;
  platform: StorePlatform;
  domain: string;
  currency: string;
  timezone: string;
  status: 'pending' | 'active' | 'disconnected' | 'error';
  lastEventAt: string | null;
  createdAt: string;
  counts?: { products: number; orders: number; customers: number; failedEvents: number };
}

export interface ApiCredentialDto {
  id: string;
  storeId: string;
  kind: 'secret' | 'publishable';
  name: string;
  keyPrefix: string;
  scopes: string[];
  allowedOrigins: string[] | null;
  lastUsedAt: string | null;
  revokedAt: string | null;
  /** Set on a rotated key: it keeps working until this time. */
  expiresAt: string | null;
  createdAt: string;
}

/** Returned once when a credential is created or rotated. */
export interface ApiCredentialSecretsDto {
  credential: ApiCredentialDto;
  apiKey: string;
  signingSecret: string | null;
}

export interface WebhookEndpointDto {
  id: string;
  storeId: string | null;
  url: string;
  description: string;
  events: string[];
  isActive: boolean;
  disabledReason: string | null;
  createdAt: string;
}
